import {AfterViewInit, Directive, inject} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {CustomToastService} from '../../@core/utils/custom-toast.service';
import {isDeepEqual} from '../../@core/utils/forms/formvalue-utils';

/**
 * Abstrakte Basisklasse für formularbasierte Modal-Komponenten.
 * Stellt gemeinsame Logik für save() und cancel() bereit.
 */
@Directive()
export abstract class AbstractFormModalComponent implements AfterViewInit {
  protected readonly activeModal = inject(NgbActiveModal);
  protected readonly customToastService = inject(CustomToastService);
  protected readonly translateService = inject(TranslateService);

  /** Das reaktive Formular der Subklasse */
  protected abstract form: AbstractControl;

  /** i18n-Schlüssel für die Validierungsfehlermeldung */
  protected abstract validationErrorKey: string;

  /** Gibt den Wert zurück, mit dem das Modal geschlossen wird */
  protected abstract getCloseValue(): unknown;

  private _initialValue: Record<string, unknown> | null = null;

  ngAfterViewInit(): void {
    this._initialValue = JSON.parse(JSON.stringify(this.form?.value ?? null));
  }

  get saveDisabled(): boolean {
    if (!this.form?.dirty) return true;
    if (this.form.invalid) return true;
    if (this._initialValue !== null && isDeepEqual(this.form.value, this._initialValue)) return true;
    return hasBlankStringValues(this.form.value);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    if (this.form.valid) {
      this.activeModal.close(this.getCloseValue());
    } else {
      this.form.markAllAsTouched();
      this.customToastService.showError(
        this.translateService.instant(this.validationErrorKey)
      );
    }
  }
}

/**
 * Prüft rekursiv, ob ein Formularwert ausschließlich aus Leerzeichen besteht.
 */
function hasBlankStringValues(value: unknown): boolean {
  if (typeof value === 'string') return value.length > 0 && value.trim().length === 0;
  if (value !== null && typeof value === 'object') {
    return Object.values(value).some(v => hasBlankStringValues(v));
  }
  return false;
}

